import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Check, Plus } from 'lucide-react'
import { ControlBox } from '@chakra-ui/core'
import { Button, Chip, SearchInput } from 'components/ui'
import { RootState } from 'constants/interfaces'
import { isRtlSelector } from 'redux/selectors/general'
import { useTaskCategories } from 'components/TasksV2/hooks/useTaskCategories'
import { categoryOptions, normalizeCategoryName, parseTaskCategories } from 'components/TasksV2/model/categoryNames'
import { useCategoryPickerText } from './strings'
import styles from './styles.module.css'

export interface CategoryPickerProps {
  existingNames: string[]
  onConfirm: (names: string[]) => void
  onCancel?: () => void
  disabled?: boolean
}

export function CategoryPicker({ existingNames, onConfirm, onCancel, disabled = false }: CategoryPickerProps) {
  const rid = useSelector((state: RootState) => state.config.config.rid)
  const rtl = useSelector(isRtlSelector)
  const { categories, saveCategories } = useTaskCategories()
  const t = useCategoryPickerText()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const options = parseTaskCategories([...categoryOptions(rid, categories), ...existingNames.map((name) => ({ name }))])
  const added = new Set(existingNames.map(normalizeCategoryName))
  const available = options.filter((option) => !added.has(normalizeCategoryName(option.name)))
  const choices = available.filter((option) => selected.includes(normalizeCategoryName(option.name)))
  const query = normalizeCategoryName(search)
  const shown = options.filter((option) => normalizeCategoryName(option.name).includes(query))
  const canCreate = !!query && !options.some((option) => normalizeCategoryName(option.name) === query)

  const create = () => {
    if (disabled || !canCreate) return
    saveCategories([...categories, { name: search.trim() }])
    setSelected((previous) => [...previous, query])
    setSearch('')
  }

  return (
    <section className={styles.picker} data-inline={!onCancel} dir={rtl ? 'rtl' : 'ltr'} aria-label={t.title}>
      {onCancel && (
        <header>
          <h3>{t.title}</h3>
          <p>{t.hint}</p>
        </header>
      )}
      <div className={styles.selection}>
        <SearchInput
          aria-label={t.search}
          variant="ghost"
          elementHeight="52px"
          border="0"
          borderBottom="0"
          borderRadius="0"
          pl="36px"
          pr="36px"
          _focus={{ boxShadow: 'inset 0 -2px 0 #25292d', outline: 'none' }}
          placeholder={t.search}
          direction={rtl ? 'rtl' : 'ltr'}
          value={search}
          isDisabled={disabled}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
              event.preventDefault()
              create()
            }
          }}
        />
        <div className={styles.options}>
          {shown.map((option) => {
            const key = normalizeCategoryName(option.name)
            const exists = added.has(key)
            return (
              <label key={key} className={styles.option} data-selected={selected.includes(key)}>
                <input
                  className={styles.checkboxInput}
                  type="checkbox"
                  aria-label={option.name}
                  checked={exists || selected.includes(key)}
                  disabled={disabled || exists}
                  onChange={() =>
                    setSelected((previous) =>
                      previous.includes(key) ? previous.filter((item) => item !== key) : [...previous, key]
                    )
                  }
                />
                <ControlBox
                  width="20px"
                  height="20px"
                  border="2px solid"
                  borderColor="#cdd0d6"
                  borderRadius="5px"
                  bg="white"
                  color="white"
                  _checked={{ bg: '#22272a', borderColor: '#22272a' }}
                  _checkedAndDisabled={{ bg: '#d7d9de', borderColor: '#d7d9de' }}
                  _focus={{ boxShadow: '0 0 0 3px rgba(34,39,42,0.15)' }}
                >
                  <Check size={14} strokeWidth={3} />
                </ControlBox>
                <span className={styles.optionName} dir="auto">
                  {option.name}
                </span>
                {exists && <Chip label={<>{t.alreadyAdded}</>} size="sm" color="#74808b" bg="#f0f2f4" border="none" />}
              </label>
            )
          })}
          {canCreate && (
            <Button
              type="button"
              variant="ghost"
              className={styles.create}
              aria-label={t.create.replace('{name}', search.trim())}
              isDisabled={disabled}
              startIcon={
                <span className={styles.createIcon} aria-hidden="true">
                  <Plus size={16} strokeWidth={2.5} />
                </span>
              }
              onClick={create}
            >
              <span>
                {t.create.split('{name}')[0]}
                <strong>{search.trim()}</strong>
                {t.create.split('{name}')[1]}
              </span>
            </Button>
          )}
        </div>
      </div>
      <footer>
        <span role="status">{choices.length ? t.selected.replace('{count}', String(choices.length)) : t.nothing}</span>
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            height="44px"
            px="18px"
            borderRadius="9px"
            borderColor="#e2e4e9"
            color="#22272a"
            boxShadow="none"
            isDisabled={disabled}
            onClick={onCancel}
          >
            {t.cancel}
          </Button>
        )}
        <Button
          type="button"
          variant="primary"
          height="44px"
          px="18px"
          borderRadius="9px"
          bg="#22272a"
          boxShadow="none"
          _disabled={{
            bg: '#b5b8bf',
            color: 'white',
            opacity: 1,
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: 'none',
          }}
          isDisabled={disabled || !choices.length}
          onClick={() => {
            if (!disabled && choices.length) onConfirm(choices.map((option) => option.name))
          }}
        >
          {choices.length > 1 ? t.addMany.replace('{count}', String(choices.length)) : t.addOne}
        </Button>
      </footer>
    </section>
  )
}
